import { SpinnerUntil } from "@edgetier/components";
import { faListUl } from "@fortawesome/pro-solid-svg-icons";
import { FunctionComponent, memo } from "react";
import { Box } from "../../box";
import { EmptyState } from "../../pre-built/empty-state";
import { Timeline } from "../../timeline";
import { ChangelogEntry } from "./changelog-entry";
import { IChangelogEntry, IProps } from "./changelog.types";

/**
 * Displays a changelog as a vertical timeline, one item per change event.
 *
 * Presentational only: the consumer fetches its data and maps it into normalized
 * {@link IChangelogEntry} objects (the `changelog.utilities` helpers cover author and
 * timestamp formatting). This keeps the component decoupled from any specific event source.
 *
 * @param props.entries - The entries to display, ordered most recent first.
 * @param props.isLoading - Whether the entries are still loading.
 * @param props.isError - Whether loading the entries failed.
 * @param props.emptyTitle - Title shown when there are no entries.
 * @param props.errorTitle - Title shown when loading failed.
 */
const ChangelogComponent: FunctionComponent<IProps> = ({
    entries,
    isLoading = false,
    isError = false,
    emptyTitle = "No changes found",
    errorTitle = "Unable to load changes",
}) => {
    if (isError) {
        return <EmptyState height="100%" width="100%" icon={faListUl} title={errorTitle} />;
    }

    if (!isLoading && entries.length === 0) {
        return <EmptyState height="100%" width="100%" icon={faListUl} title={emptyTitle} />;
    }

    return (
        <SpinnerUntil<readonly IChangelogEntry[]> data={entries} isReady={!isLoading}>
            {(readyEntries) => (
                <Box p="sm" w="100%">
                    <Timeline variant="changelog" bulletSize={24} lineWidth={2}>
                        {readyEntries.map((entry) => (
                            <ChangelogEntry key={entry.key} entry={entry} />
                        ))}
                    </Timeline>
                </Box>
            )}
        </SpinnerUntil>
    );
};

const Changelog = memo(ChangelogComponent);

export { Changelog };
